<script setup lang="ts">
import { computed } from 'vue'

import type { AssistantSend, ChatMessage } from '../models/orpheus.types'
import { messageStamp } from '../service/messageStamp'
import type { RecommendationDecisionIntent } from '../service/recommendationDecision'

import AssistantMessage from './AssistantMessage.vue'
import OrpheusUserAvatar from './OrpheusUserAvatar.vue'
import UserMessage from './UserMessage.vue'

import { formatCredits } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Icon } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    message: ChatMessage
    compact?: boolean
    editing?: boolean
    editDraft?: string
    copied?: boolean
    busy?: boolean
    activeAnswer?: boolean
    latest?: boolean
    latestCard?: boolean
    unavailableOptions?: Record<string, Record<string, string>>
  }>(),
  {
    compact: false,
    editing: false,
    editDraft: '',
    copied: false,
    busy: false,
    activeAnswer: false,
    latest: false,
    latestCard: false,
    unavailableOptions: () => ({}),
  },
)
const emit = defineEmits<{
  copy: []
  edit: []
  retry: []
  report: []
  cancel: []
  'open-answer': []
  'open-file-preview': []
  'open-key-settings': []
  'answer-with-credits': []
  send: [payload: AssistantSend]
  'confirm-action': [intent: RecommendationDecisionIntent]
  'cancel-edit': []
  'submit-edit': []
  'update:edit-draft': [value: string]
}>()
const { t } = useI18n()
const format = useFormat()

const user = computed(() => props.message.role === 'user')
const timestamp = computed(() => messageStamp(props.message.createdAt, format))
const inFlight = computed(
  () => props.message.streamState === 'connecting' || props.message.streamState === 'streaming',
)
const settledAssistant = computed(() => !user.value && !inFlight.value)
const retryable = computed(
  () =>
    !user.value &&
    !props.message.keyError &&
    (props.message.streamState === 'reconnectable-error' ||
      props.message.streamState === 'terminal-error'),
)
/**
 * old suppressed the whole strip while a turn was still arriving (`v-if="!message.waiting"`) — a
 * timestamp and a copy button on an empty answer are controls for content that is not there yet.
 */
// The action strip belongs to assistant answers. It stays out of the user's way until the
// assistant bubble is hovered or focused, matching the reference conversation chrome.
const showMeta = computed(() => !props.editing && !inFlight.value)
/** The key-error card is the answer: it draws its own surface, so the bubble steps aside (v3). */
const bare = computed(() => Boolean(props.message.keyError))
/**
 * "Used 3 credits · 412 left" (v3 ORP-24542): what THIS answer cost, kept on the message so a later
 * answer — or a reload — never changes it.
 */
const creditsLabel = computed(() => {
  const credits = props.message.credits
  if (user.value || !credits) return ''
  const charged = Math.max(0, Math.round(credits.charged))
  const used = t(charged === 1 ? 'orpheus.message.creditsUsedOne' : 'orpheus.message.creditsUsed', {
    count: formatCredits(charged),
  })
  return credits.remaining === null
    ? used
    : `${used} · ${t('orpheus.message.creditsLeft', { count: formatCredits(credits.remaining) })}`
})

/**
 * old's action strip is a row of 1.75rem square hit areas (`OrpheusChatArea.vue:303`) with a 4px
 * hover, not a pill. Carried on each control rather than as a `[&_.ui-btn]` descendant rule so the
 * wider report control below cannot end up in a specificity fight with it.
 */
const ICON_CONTROL =
  'inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0 text-orpheus-caption transition-colors hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50'
</script>

<template>
  <article
    class="message-bubble group grid max-w-full justify-items-start gap-x-2 gap-y-2"
    :class="[
      compact ? 'w-full' : 'w-5/7 max-sm:w-full',
      user
        ? 'message-bubble--user grid-cols-[minmax(0,_1fr)_auto] justify-self-end justify-items-end'
        : 'grid-cols-[auto_minmax(0,_1fr)]',
    ]"
  >
    <!-- Brand mark stays aligned with the top of a long answer. -->
    <span
      v-if="!user"
      class="message-bubble__avatar grid row-1 self-start place-items-center text-orpheus-brand"
      aria-hidden="true"
    >
      <img src="@/shared/assets/logos/orpheus_logo.png" alt="" class="size-8 object-contain" />
    </span>
    <div
      class="message-bubble__body w-full text-base leading-relaxed"
      :class="[
        user ? 'col-1' : 'col-2',
        bare ? 'flex' : 'py-3 px-md border border-border/30 rounded-2xl shadow-sm',
        user
          ? 'rounded-tr-none bg-orpheus-user-bubble'
          : 'rounded-tl-none bg-orpheus-assistant-bubble',
      ]"
    >
      <UserMessage
        v-if="user"
        :message="message"
        :editing="editing"
        :busy="busy"
        :draft="editDraft"
        @update:draft="emit('update:edit-draft', $event)"
        @cancel-edit="emit('cancel-edit')"
        @submit-edit="emit('submit-edit')"
      />
      <AssistantMessage
        v-else
        class="w-full"
        :message="message"
        :active-answer="activeAnswer"
        :busy="busy"
        :latest="latest"
        :latest-card="latestCard"
        :unavailable-options="unavailableOptions"
        @cancel="emit('cancel')"
        @retry="emit('retry')"
        @send="emit('send', $event)"
        @answer-with-credits="emit('answer-with-credits')"
        @open-key-settings="emit('open-key-settings')"
        @open-answer="emit('open-answer')"
        @open-file-preview="emit('open-file-preview')"
        @confirm-action="emit('confirm-action', $event)"
      />
    </div>
    <OrpheusUserAvatar v-if="user" class="col-2 row-1" />
    <!-- old's row is `gap-2` with the controls inside it `gap-1`, and the stamp at `text-2xs`
         (`OrpheusChatArea.vue:278,:299,:347`). No hover reveal: old gated the assistant's report
         control and stamp behind `group-hover`, which hid a reporting affordance from anyone not
         moving a pointer over the turn. -->
    <div
      v-if="showMeta"
      class="message-bubble__meta flex min-h-7 flex-wrap items-center gap-2 text-orpheus-caption text-xs transition-opacity duration-150"
      :class="[
        user ? 'col-1 justify-self-end px-1 pointer-events-none opacity-0' : 'col-2 px-2',
        !user ? 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100' : '',
      ]"
    >
      <span
        v-if="creditsLabel"
        class="flex min-w-0 max-w-full items-center gap-1 text-xs font-semibold text-primary-muted"
        data-testid="orpheus-message-credits"
      >
        <Icon name="coins" :size="10" weight="light" class="shrink-0" aria-hidden="true" />
        <span class="truncate">{{ creditsLabel }}</span>
      </span>
      <!-- Order follows old: the controls first, then the timestamp at the end of the row. Each is
           icon-only, with the label in `aria-label` and the tooltip, so the strip stays a strip
           instead of three word-buttons. -->
      <div class="message-bubble__actions flex items-center gap-2">
        <button
          v-if="user"
          type="button"
          :class="ICON_CONTROL"
          :disabled="busy"
          :title="t('orpheus.message.edit')"
          :aria-label="t('orpheus.message.edit')"
          @click="emit('edit')"
        >
          <Icon name="pen-to-square" :size="14" weight="light" aria-hidden="true" />
        </button>
        <button
          v-if="user"
          type="button"
          :class="ICON_CONTROL"
          :title="t(copied ? 'orpheus.message.copied' : 'orpheus.message.copy')"
          :aria-label="t(copied ? 'orpheus.message.copied' : 'orpheus.message.copy')"
          @click="emit('copy')"
        >
          <Icon :name="copied ? 'check' : 'copy'" :size="14" weight="light" aria-hidden="true" />
        </button>
        <button
          v-if="retryable"
          type="button"
          :class="ICON_CONTROL"
          :disabled="busy"
          :title="t('orpheus.message.retry')"
          :aria-label="t('orpheus.message.retry')"
          @click="emit('retry')"
        >
          <Icon name="rotate-left" :size="14" weight="light" aria-hidden="true" />
        </button>
        <!-- The one labelled control in the row, as in old: an icon plus "Report Issue"
             (`OrpheusChatArea.vue:285-291`). -->
        <button
          v-if="settledAssistant"
          type="button"
          class="message-bubble__report inline-flex h-7 cursor-pointer items-center gap-1 rounded-sm border-0 bg-transparent px-1.5 py-0 text-xs font-semibold text-orpheus-caption transition-colors hover:text-danger focus-visible:outline-2 focus-visible:outline-ring"
          :title="t('orpheus.message.reportIssue')"
          @click="emit('report')"
        >
          <Icon name="alert-triangle" :size="12" weight="light" aria-hidden="true" />
          {{ t('orpheus.message.reportIssue') }}
        </button>
      </div>
      <!-- old's middle dot between the report control and the stamp (`OrpheusChatArea.vue:288`). -->
      <span
        v-if="settledAssistant && timestamp"
        class="message-bubble__dot select-none text-border-strong"
        aria-hidden="true"
        >&bull;</span
      >
      <time
        v-if="timestamp"
        class="text-xs font-medium"
        :datetime="message.createdAt ?? undefined"
        >{{ timestamp }}</time
      >
    </div>
  </article>
</template>
